import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, FileDown, Wrench, AlertTriangle, CheckCircle2, Lock, Unlock, Trash2, PlayCircle, PenLine, LayoutGrid, Tablet, Smartphone } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AutosaveIndicator, QcrStatusBadge } from '../components/AutosaveIndicator';
import { SignaturePad } from '../components/SignaturePad';
import { useQcrAutosave, readPendingCache } from '../hooks/useQcrAutosave';
import { SingleSampleView } from '../components/qcr/SingleSampleView';
import { useAuth } from '../context/AuthContext';
import { qualityControlReportService } from '../services/api';

const SAMPLES_PER_PAGE = 10;
const SAMPLES_PER_PAGE_TABLET = 6;
const VIEW_KEY = 'qcr_view_mode';

const detectDefaultView = () => {
  const saved = localStorage.getItem(VIEW_KEY);
  if (saved) return saved;
  if (typeof window === 'undefined') return 'grid';
  const touch = window.matchMedia?.('(pointer: coarse)')?.matches;
  if (window.innerWidth < 768) return 'single';
  if (touch && window.innerWidth < 1280) return 'tablet';
  return 'grid';
};
const LOCKED = ['Completato', 'Annullato'];

function resultBadge(r) {
  if (r === 'OK') return <Badge className="bg-emerald-600 text-white text-[10px] px-1.5">OK</Badge>;
  if (r === 'NOK') return <Badge className="bg-red-600 text-white text-[10px] px-1.5">NOK</Badge>;
  return <Badge className="bg-slate-200 text-slate-600 text-[10px] px-1.5">—</Badge>;
}

export function statusBadge(status) {
  return <QcrStatusBadge status={status} />;
}

const cellKey = (sampleId, rowId) => `${sampleId}::${rowId}`;

export default function QualityControlReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole('admin', 'quality');

  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageIdx, setPageIdx] = useState(0);
  const [values, setValues] = useState({});            // cellKey -> raw input string
  const [serverStamp, setServerStamp] = useState({});  // cellKey -> updated_at seen at load
  const [conflict, setConflict] = useState(null);
  const [incomplete, setIncomplete] = useState(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [notes, setNotes] = useState('');
  const [pcOpen, setPcOpen] = useState(null);
  const [pcForm, setPcForm] = useState({ requested_action: '', priority: 'normal', deadline: '', ko_note: '' });
  const [signOpen, setSignOpen] = useState(null);   // 'inspector' | 'quality_manager'
  const [signData, setSignData] = useState(null);
  const [active, setActive] = useState({ r: -1, c: -1 });
  const [viewMode, setViewModeState] = useState(detectDefaultView);
  const [sampleIdx, setSampleIdx] = useState(0);
  const [showHeader, setShowHeader] = useState(false);
  const cellRefs = useRef({});
  const escapedRef = useRef(null);
  const notesTimer = useRef(null);

  const applySaved = useCallback((data) => {
    if (!data) return;
    setRep(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      const incoming = data.measurement ? [data.measurement] : (data.measurements || []);
      if (incoming.length) {
        const list = [...(prev.measurements || [])];
        incoming.forEach(m => {
          const i = list.findIndex(x => x.sample_id === m.sample_id && x.control_plan_row_id === m.control_plan_row_id);
          if (i >= 0) list[i] = m; else list.push(m);
        });
        next.measurements = list;
        setServerStamp(s => {
          const copy = { ...s };
          incoming.forEach(m => { copy[cellKey(m.sample_id, m.control_plan_row_id)] = m.updated_at; });
          return copy;
        });
      }
      if (data.sample_result !== undefined && incoming[0]) {
        next.samples = (prev.samples || []).map(s => s.id === incoming[0].sample_id ? { ...s, sample_result: data.sample_result } : s);
      }
      if (data.overall_result !== undefined) next.overall_result = data.overall_result;
      if (data.status) next.status = data.status;
      if (data.last_saved_at) next.last_saved_at = data.last_saved_at;
      return next;
    });
  }, []);

  const autosave = useQcrAutosave(id, {
    onSaved: applySaved,
    onConflict: (c) => setConflict(c),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qualityControlReportService.getById(id);
      const data = res.data;
      setRep(data);
      setNotes(data.control_notes || '');
      const v = {};
      const stamps = {};
      (data.measurements || []).forEach(m => {
        const k = cellKey(m.sample_id, m.control_plan_row_id);
        stamps[k] = m.updated_at;
        if (m.measured_numeric_value !== null && m.measured_numeric_value !== undefined) v[k] = String(m.measured_numeric_value);
        else if (m.measured_text_value) v[k] = m.measured_text_value;
        else v[k] = '';
      });
      // Restore locally cached unsynchronized values (they are newer by definition)
      const cached = readPendingCache(id);
      const cachedKeys = Object.keys(cached);
      cachedKeys.forEach(k => {
        const p = cached[k];
        v[k] = p.measured_numeric_value !== null && p.measured_numeric_value !== undefined
          ? String(p.measured_numeric_value)
          : (p.measured_text_value || '');
      });
      setValues(v);
      setServerStamp(stamps);
      if (cachedKeys.length) {
        autosave.restoreCached();
        toast.info(`${cachedKeys.length} misure recuperate dalla cache locale — sincronizzazione in corso…`);
        autosave.flushAll();
      } else if (data.last_saved_at) {
        autosave.setLastSavedAt(new Date(data.last_saved_at));
      }
      const filled = data.measurements_filled || 0;
      const total = data.measurements_total || 0;
      if (!LOCKED.includes(data.status) && filled > 0 && filled < total) setShowRecovery(true);
    } catch { toast.error('Errore caricamento report'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const planRows = useMemo(() => rep?.plan_rows || [], [rep]);
  const samples = useMemo(() => rep?.samples || [], [rep]);
  const measurementsMap = useMemo(() => {
    const m = {};
    (rep?.measurements || []).forEach(mm => { m[cellKey(mm.sample_id, mm.control_plan_row_id)] = mm; });
    return m;
  }, [rep]);

  const locked = LOCKED.includes(rep?.status);
  const perPage = viewMode === 'tablet' ? SAMPLES_PER_PAGE_TABLET : SAMPLES_PER_PAGE;
  const big = viewMode === 'tablet';
  const totalPages = Math.max(1, Math.ceil(samples.length / perPage));
  const visibleSamples = useMemo(() => {
    const start = pageIdx * perPage;
    return samples.slice(start, start + perPage);
  }, [samples, pageIdx, perPage]);

  const setViewMode = (mode) => {
    setViewModeState(mode);
    localStorage.setItem(VIEW_KEY, mode);
    setPageIdx(0);
  };

  const buildPayload = (sampleId, row, raw) => {
    const isNumeric = (row.tipo_valutazione || '').toLowerCase().includes('numerica');
    const payload = {
      sample_id: sampleId,
      control_plan_row_id: row.id,
      measured_numeric_value: null,
      measured_text_value: null,
      notes: measurementsMap[cellKey(sampleId, row.id)]?.notes || null,
      base_updated_at: serverStamp[cellKey(sampleId, row.id)] || null,
    };
    if (isNumeric) {
      const parsed = raw === '' ? null : Number(String(raw).replace(',', '.'));
      payload.measured_numeric_value = parsed === null || isNaN(parsed) ? null : parsed;
    } else {
      payload.measured_text_value = raw || null;
    }
    return payload;
  };

  const onCellChange = (sampleId, row, raw) => {
    const k = cellKey(sampleId, row.id);
    setValues(prev => ({ ...prev, [k]: raw }));
    autosave.queue(k, buildPayload(sampleId, row, raw));
  };

  const onCellBlur = (sampleId, row) => {
    const k = cellKey(sampleId, row.id);
    if (escapedRef.current === k) { escapedRef.current = null; return; }
    autosave.flushKey(k);
  };

  // ─── Keyboard navigation across the measurement grid ───────────────────────
  const focusCell = useCallback((r, c) => {
    const el = cellRefs.current[`${r}:${c}`];
    if (!el) return false;
    el.focus();
    el.select?.();
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return true;
  }, []);

  const moveTo = useCallback((r, c, maxR, maxC) => {
    if (r < 0 || r > maxR) return;
    if (c < 0) {
      // step back to the previous page of samples
      if (pageIdx === 0) return;
      setPageIdx(pageIdx - 1);
      setTimeout(() => focusCell(r, perPage - 1), 120);
      return;
    }
    if (c > maxC) {
      if (pageIdx >= totalPages - 1) return;
      setPageIdx(pageIdx + 1);
      setTimeout(() => focusCell(r, 0), 120);
      return;
    }
    focusCell(r, c);
  }, [focusCell, pageIdx, totalPages, perPage]);

  const nextEmptyFrom = useCallback((rowIdx, colIdx) => {
    const flatIndex = (r, c) => r * samples.length + c;
    const start = flatIndex(rowIdx, pageIdx * perPage + colIdx) + 1;
    for (let i = start; i < planRows.length * samples.length; i += 1) {
      const r = Math.floor(i / samples.length);
      const absC = i % samples.length;
      const k = cellKey(samples[absC].id, planRows[r].id);
      if (!values[k]) {
        const targetPage = Math.floor(absC / perPage);
        if (targetPage !== pageIdx) {
          setPageIdx(targetPage);
          setTimeout(() => focusCell(r, absC % perPage), 120);
        } else {
          focusCell(r, absC % perPage);
        }
        return true;
      }
    }
    toast.info('Nessuna misura vuota successiva');
    return false;
  }, [samples, planRows, values, pageIdx, focusCell, perPage]);

  const onCellKeyDown = (e, rowIdx, colIdx, sample, row) => {
    const maxR = planRows.length - 1;
    const maxC = visibleSamples.length - 1;
    const mod = e.ctrlKey || e.metaKey;
    if (e.key === 'Enter') {
      e.preventDefault();
      autosave.flushKey(cellKey(sample.id, row.id));
      if (mod) nextEmptyFrom(rowIdx, colIdx);
      else moveTo(e.shiftKey ? rowIdx - 1 : rowIdx + 1, colIdx, maxR, maxC);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveTo(e.key === 'ArrowDown' ? rowIdx + 1 : rowIdx - 1, colIdx, maxR, maxC);
      return;
    }
    if (mod && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
      e.preventDefault();
      moveTo(rowIdx, e.key === 'ArrowRight' ? colIdx + 1 : colIdx - 1, maxR, maxC);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      const k = cellKey(sample.id, row.id);
      autosave.cancelKey(k);                 // drop the queued value: Esc must NOT save
      escapedRef.current = k;                // and the blur that follows must not flush it
      const saved = measurementsMap[k];
      const original = saved?.measured_numeric_value ?? saved?.measured_text_value ?? '';
      setValues(prev => ({ ...prev, [k]: original === null ? '' : String(original) }));
      e.currentTarget.blur();
    }
  };

  const saveNotes = (val) => {
    setNotes(val);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      try {
        autosave.setStatus('saving');
        const res = await qualityControlReportService.update(id, { control_notes: val });
        autosave.setLastSavedAt(new Date());
        autosave.setStatus('saved');
        setRep(prev => prev ? { ...prev, status: res.data.status, control_notes: res.data.control_notes } : prev);
      } catch { autosave.setStatus('error'); }
    }, 700);
  };

  const updateSampleField = async (sample, patch) => {
    try {
      autosave.setStatus('saving');
      const res = await qualityControlReportService.updateSample(id, sample.id, patch);
      autosave.setLastSavedAt(new Date());
      autosave.setStatus('saved');
      setRep(prev => prev ? {
        ...prev,
        status: res.data.status || prev.status,
        samples: prev.samples.map(s => s.id === sample.id ? { ...s, ...patch } : s),
      } : prev);
    } catch { autosave.setStatus('error'); }
  };

  const completeReport = async () => {
    await autosave.flushAll();
    if (autosave.pendingCount > 0) return toast.error('Attendi la sincronizzazione delle misure in coda');
    try {
      const res = await qualityControlReportService.complete(id);
      setRep(prev => ({ ...prev, ...res.data }));
      toast.success(`Report completato — esito ${res.data.overall_result || 'n/d'}`);
      setShowRecovery(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'report_incomplete') setIncomplete(detail);
      else toast.error(typeof detail === 'string' ? detail : 'Errore completamento report');
    }
  };

  const reopenReport = async () => {
    try {
      const res = await qualityControlReportService.reopen(id);
      setRep(prev => ({ ...prev, ...res.data, signatures: res.data.signatures || {} }));
      toast.success('Report riaperto');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const cancelReport = async () => {
    if (!window.confirm('Annullare definitivamente questo report?')) return;
    try {
      const res = await qualityControlReportService.cancel(id);
      setRep(prev => ({ ...prev, ...res.data }));
      toast.success('Report annullato');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const deleteDraft = async () => {
    if (!window.confirm(`Eliminare la bozza ${rep.report_number}? I dati inseriti verranno persi.`)) return;
    try {
      await qualityControlReportService.remove(id);
      localStorage.removeItem(`qcr_pending_${id}`);
      toast.success('Bozza eliminata');
      navigate('/quality-control-reports');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const focusFirstEmpty = () => {
    setShowRecovery(false);
    for (let si = 0; si < samples.length; si += 1) {
      for (const row of planRows) {
        const k = cellKey(samples[si].id, row.id);
        if (!values[k]) {
          if (viewMode === 'single') setSampleIdx(si);
          else setPageIdx(Math.floor(si / perPage));
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="qcr-cell-${samples[si].sample_number}-${row.numero_caratteristica}"]`);
            el?.focus();
            el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 150);
          return;
        }
      }
    }
    toast.info('Tutte le misure sono compilate: puoi completare il report');
  };

  // Single-sample view: Enter moves to the next characteristic of the same sample
  const onSingleKeyDown = (e, rowIdx, row, sample) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      autosave.flushKey(cellKey(sample.id, row.id));
      const target = cellRefs.current[`${e.shiftKey ? rowIdx - 1 : rowIdx + 1}:single`];
      if (target) { target.focus(); target.select?.(); target.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      const k = cellKey(sample.id, row.id);
      autosave.cancelKey(k);
      escapedRef.current = k;
      const saved = measurementsMap[k];
      const original = saved?.measured_numeric_value ?? saved?.measured_text_value ?? '';
      setValues(prev => ({ ...prev, [k]: original === null ? '' : String(original) }));
      e.currentTarget.blur();
    }
  };

  const nextEmptyInSample = () => {
    const current = samples[sampleIdx];
    const idx = planRows.findIndex(r => !values[cellKey(current.id, r.id)]);
    if (idx >= 0) {
      const el = cellRefs.current[`${idx}:single`];
      el?.focus(); el?.select?.(); el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    // look forward first, then wrap around to earlier samples
    const hasEmpty = s => planRows.some(r => !values[cellKey(s.id, r.id)]);
    let target = samples.findIndex((s, i) => i > sampleIdx && hasEmpty(s));
    if (target < 0) target = samples.findIndex((s, i) => i < sampleIdx && hasEmpty(s));
    if (target < 0) { toast.info('Tutte le misure sono compilate'); return; }
    setSampleIdx(target);
    toast.info(`Passo al campione C${String(samples[target].sample_number).padStart(2, '0')}`);
    setTimeout(() => {
      const firstEmpty = planRows.findIndex(r => !values[cellKey(samples[target].id, r.id)]);
      const el = cellRefs.current[`${Math.max(0, firstEmpty)}:single`];
      el?.focus(); el?.select?.(); el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 200);
  };

  const resolveConflict = async (keep) => {
    const c = conflict;
    setConflict(null);
    if (!c) return;
    if (keep === 'mine') {
      try {
        await autosave.forceSave(c.incoming);
        toast.success('Valore locale mantenuto');
        load();
      } catch { toast.error('Errore salvataggio'); }
    } else {
      const sm = c.server_measurement;
      const k = cellKey(sm.sample_id, sm.control_plan_row_id);
      setValues(prev => ({
        ...prev,
        [k]: sm.measured_numeric_value !== null && sm.measured_numeric_value !== undefined ? String(sm.measured_numeric_value) : (sm.measured_text_value || ''),
      }));
      setServerStamp(prev => ({ ...prev, [k]: sm.updated_at }));
      applySaved({ measurement: sm });
      toast.info('Mantenuto il valore presente sul server');
    }
  };

  const submitSignature = async () => {
    if (!signData) return toast.error('Disegna la firma prima di confermare');
    try {
      const res = await qualityControlReportService.sign(id, { role: signOpen, image_base64: signData });
      setRep(prev => ({ ...prev, ...res.data, signatures: res.data.signatures || {} }));
      setSignOpen(null);
      setSignData(null);
      toast.success('Report firmato');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Errore firma report');
    }
  };

  const removeSignature = async (role) => {
    if (!window.confirm('Rimuovere la firma? Il report dovrà essere firmato di nuovo.')) return;
    try {
      const res = await qualityControlReportService.deleteSignature(id, role);
      setRep(prev => ({ ...prev, ...res.data, signatures: res.data.signatures || {} }));
      toast.success('Firma rimossa');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const downloadPdf = async () => {
    try {
      const res = await qualityControlReportService.pdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${rep.report_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore PDF'); }
  };

  const openPending = (measurement, row, sample) => {
    setPcOpen({ measurement, row, sample });
    setPcForm({
      requested_action: '',
      priority: 'normal',
      deadline: '',
      ko_note: `Misura ${measurement.measured_numeric_value ?? measurement.measured_text_value ?? '—'} vs nominale ${row.specifica_nominale ?? '—'} [${row.tolleranza_min ?? '—'}; ${row.tolleranza_max ?? '—'}]`,
    });
  };
  const submitPending = async () => {
    if (!pcForm.requested_action.trim()) return toast.error('Descrivi l\'azione richiesta');
    try {
      await qualityControlReportService.createPendingCollaudo(id, {
        measurement_id: pcOpen.measurement.id,
        requested_action: pcForm.requested_action,
        priority: pcForm.priority,
        deadline: pcForm.deadline || null,
        ko_note: pcForm.ko_note,
      });
      toast.success('Pending Collaudo creato');
      setPcOpen(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  if (loading || !rep) return <><div className="p-6 text-sm text-slate-500">Caricamento…</div></>;

  const overall = rep.overall_result;
  const overallBadge = overall === 'OK'
    ? <Badge className="bg-emerald-600 text-white">Esito complessivo: OK</Badge>
    : overall === 'NOK'
    ? <Badge className="bg-red-600 text-white">Esito complessivo: NOK</Badge>
    : <Badge className="bg-slate-300 text-slate-700">Esito complessivo: In corso</Badge>;

  const filled = planRows.length * samples.length
    ? Object.keys(values).filter(k => values[k] !== '' && values[k] !== undefined).length
    : 0;
  const totalCells = planRows.length * samples.length;

  return (
    <>
      <div className="space-y-4" data-testid="qcr-detail-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="h-10 md:h-9" onClick={() => navigate('/quality-control-reports')}><ArrowLeft className="w-3.5 h-3.5 mr-1" />Registro</Button>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">{rep.report_number}</h1>
            {statusBadge(rep.status)}
            {overallBadge}
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
            <AutosaveIndicator
              status={autosave.status}
              lastSavedAt={autosave.lastSavedAt}
              pendingCount={autosave.pendingCount}
              onRetry={autosave.flushAll}
            />
            <Button variant="outline" className="h-11 md:h-10 flex-1 md:flex-none" onClick={downloadPdf} data-testid="qcr-detail-pdf-btn"><FileDown className="w-4 h-4 mr-1" />Stampa</Button>
            {!locked && (
              <Button onClick={completeReport} className="h-11 md:h-10 flex-1 md:flex-none bg-emerald-700 hover:bg-emerald-800" data-testid="qcr-complete-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" />Completa Report
              </Button>
            )}
            {locked && canManage && (
              <Button variant="outline" className="h-11 md:h-10" onClick={reopenReport} data-testid="qcr-reopen-btn"><Unlock className="w-4 h-4 mr-1" />Riapri</Button>
            )}
            {!locked && canManage && (
              <Button variant="outline" className="h-11 md:h-10 text-red-700" onClick={cancelReport} data-testid="qcr-cancel-btn">Annulla report</Button>
            )}
          </div>
        </div>

        {locked && (
          <div className="flex items-center gap-2 text-xs bg-slate-100 border border-slate-300 rounded p-2" data-testid="qcr-locked-banner">
            <Lock className="w-3.5 h-3.5 text-slate-600" />
            Report <b>{rep.status}</b> — sola lettura.{canManage ? ' Usa "Riapri" per modificarlo.' : ''}
          </div>
        )}

        {showRecovery && !locked && (
          <div className="flex items-center justify-between gap-3 flex-wrap bg-amber-50 border border-amber-300 rounded p-3" data-testid="qcr-recovery-banner">
            <div className="text-sm text-amber-900">
              <b>Report incompleto – continuare la registrazione?</b>
              <div className="text-xs">{rep.measurements_filled} misure su {rep.measurements_total} già salvate sul server.</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={focusFirstEmpty} data-testid="qcr-continue-btn"><PlayCircle className="w-3.5 h-3.5 mr-1" />Continua report</Button>
              <Button size="sm" variant="outline" onClick={() => setShowRecovery(false)} data-testid="qcr-open-btn">Apri report</Button>
              {canManage && (
                <Button size="sm" variant="outline" className="text-red-700" onClick={deleteDraft} data-testid="qcr-delete-draft-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Elimina bozza</Button>
              )}
            </div>
          </div>
        )}

        {/* HEADER */}
        <button
          type="button"
          className="md:hidden w-full h-11 rounded-lg border bg-white text-sm font-medium text-slate-700"
          onClick={() => setShowHeader(v => !v)}
          data-testid="qcr-header-toggle"
        >
          {showHeader ? 'Nascondi dettagli report' : 'Mostra dettagli report'}
        </button>
        <div className={`${showHeader ? 'grid' : 'hidden'} md:grid bg-white rounded border p-3 grid-cols-2 md:grid-cols-4 gap-3 text-xs`}>
          <div><Label className="text-[10px] text-slate-500">Data</Label><div className="font-semibold">{rep.control_date}</div></div>
          <div><Label className="text-[10px] text-slate-500">Cliente</Label><div className="font-semibold">{rep.customer_name || '—'}</div></div>
          <div><Label className="text-[10px] text-slate-500">Prodotto</Label><div className="font-semibold font-mono">{rep.product_code}</div><div className="text-slate-500">{rep.product_name}</div></div>
          <div><Label className="text-[10px] text-slate-500">Revisione</Label><div className="font-semibold">{rep.revision_code || '—'}</div></div>
          <div><Label className="text-[10px] text-slate-500">Lotto / Commessa</Label><div className="font-semibold font-mono">{rep.lot_code || '—'}</div></div>
          <div><Label className="text-[10px] text-slate-500">Piano Controllo</Label><div className="font-semibold">{rep.control_plan_title || '—'} rev. {rep.control_plan_revision || '—'}</div></div>
          <div><Label className="text-[10px] text-slate-500">Situazione controllo</Label><div className="font-semibold"><Badge className="bg-indigo-600 text-white text-[10px]">{rep.control_type_label || '—'}</Badge></div></div>
          <div><Label className="text-[10px] text-slate-500">Campioni / Avanzamento</Label><div className="font-semibold text-indigo-700" data-testid="qcr-progress">{rep.sample_count} camp. — {filled}/{totalCells} misure</div></div>
          <div><Label className="text-[10px] text-slate-500">Ispettore</Label><div className="font-semibold">{rep.inspector_name || '—'}</div></div>
          <div><Label className="text-[10px] text-slate-500">Ultima modifica di</Label><div className="font-semibold" data-testid="qcr-last-editor">{rep.last_editor_name || '—'}</div></div>
          <div className="col-span-2 md:col-span-4">
            <Label className="text-[10px] text-slate-500">Note controllo (salvataggio automatico)</Label>
            <Textarea
              value={notes}
              onChange={e => saveNotes(e.target.value)}
              disabled={locked}
              rows={2}
              className="text-xs"
              placeholder="Note sul controllo…"
              data-testid="qcr-notes-input"
            />
          </div>
        </div>

        {/* VIEW SWITCHER */}
        <div className="flex items-center gap-1 bg-white border rounded-lg p-1 w-fit" data-testid="qcr-view-switcher">
          {[['grid', 'Griglia', LayoutGrid], ['tablet', 'Tablet', Tablet], ['single', 'Un campione', Smartphone]].map(([mode, label, Icon]) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-xs font-medium transition-colors ${viewMode === mode ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid={`qcr-view-${mode}`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {/* SAMPLE PAGINATION */}
        {viewMode !== 'single' && samples.length > perPage && (
          <div className="flex items-center gap-2 justify-center">
            <Button size={big ? 'default' : 'sm'} variant="outline" className={big ? 'h-12 w-12 p-0' : ''} onClick={() => setPageIdx(Math.max(0, pageIdx - 1))} disabled={pageIdx === 0}><ChevronLeft className={big ? 'w-6 h-6' : 'w-3.5 h-3.5'} /></Button>
            <span className={big ? 'text-sm font-medium' : 'text-xs'} data-testid="qcr-page-indicator">
              Campioni {visibleSamples[0]?.sample_number}–{visibleSamples[visibleSamples.length - 1]?.sample_number} di {samples.length}
            </span>
            <Button size={big ? 'default' : 'sm'} variant="outline" className={big ? 'h-12 w-12 p-0' : ''} onClick={() => setPageIdx(Math.min(totalPages - 1, pageIdx + 1))} disabled={pageIdx >= totalPages - 1}><ChevronRight className={big ? 'w-6 h-6' : 'w-3.5 h-3.5'} /></Button>
          </div>
        )}

        {/* SINGLE SAMPLE VIEW (tablet / smartphone) */}
        {viewMode === 'single' ? (
          <SingleSampleView
            sample={samples[Math.min(sampleIdx, samples.length - 1)]}
            sampleIndex={Math.min(sampleIdx, Math.max(0, samples.length - 1))}
            samples={samples}
            planRows={planRows}
            values={values}
            measurementsMap={measurementsMap}
            locked={locked}
            cellRefs={cellRefs}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyDown={onSingleKeyDown}
            onSelectSample={setSampleIdx}
            onNextEmpty={nextEmptyInSample}
            onPendingCollaudo={openPending}
            onIdentifierChange={(s, v) => updateSampleField(s, { sample_identifier: v })}
          />
        ) : (
        /* MEASUREMENT GRID */
        <div className="bg-white rounded border overflow-auto">
          <table className={`w-full border-collapse ${big ? 'text-sm' : 'text-[11px]'}`} data-testid="qcr-grid">
            <thead className="sticky top-0 z-20">
              <tr className="bg-slate-800 text-white">
                <th className={`text-left sticky left-0 bg-slate-800 z-10 ${big ? 'p-3' : 'p-1.5'}`}>#</th>
                <th className={`text-left sticky bg-slate-800 z-10 ${big ? 'p-3 left-12 min-w-[240px]' : 'p-1.5 left-8 min-w-[180px]'}`}>Caratteristica</th>
                <th className={`text-center ${big ? 'p-3' : 'p-1.5'}`}>Nominale</th>
                <th className={`text-center ${big ? 'p-3' : 'p-1.5'}`}>Min</th>
                <th className={`text-center ${big ? 'p-3' : 'p-1.5'}`}>Max</th>
                <th className={`text-center ${big ? 'p-3' : 'p-1.5'}`}>UM</th>
                {visibleSamples.map(s => (
                  <th key={s.id} className={`text-center ${big ? 'p-2 min-w-[130px]' : 'p-1.5 min-w-[80px]'}`}>
                    <div className={big ? 'text-base font-bold' : ''}>C{String(s.sample_number).padStart(2, '0')}</div>
                    <input
                      type="text"
                      defaultValue={s.sample_identifier || ''}
                      disabled={locked}
                      onBlur={e => { if ((e.target.value || '') !== (s.sample_identifier || '')) updateSampleField(s, { sample_identifier: e.target.value }); }}
                      className={`w-full rounded bg-white text-slate-700 text-center mt-1 ${big ? 'text-xs h-9 px-2' : 'text-[9px] px-1 py-0.5'}`}
                      placeholder="marcatura"
                      data-testid={`qcr-sample-ident-${s.sample_number}`}
                    />
                    <div className="mt-1">{resultBadge(s.sample_result)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {planRows.map((row, ri) => {
                const isNumeric = (row.tipo_valutazione || '').toLowerCase().includes('numerica');
                return (
                  <tr key={row.id} className={`${ri % 2 ? 'bg-slate-50' : 'bg-white'} ${active.r === ri ? 'ring-1 ring-inset ring-indigo-300' : ''}`}>
                    <td className={`font-mono sticky left-0 z-10 bg-inherit border-r border-slate-200 ${big ? 'p-3 text-base font-bold' : 'p-1.5'}`}>{row.numero_caratteristica}</td>
                    <td className={`sticky z-10 bg-inherit border-r border-slate-200 ${big ? 'p-3 left-12 min-w-[240px]' : 'p-1.5 left-8 min-w-[180px]'}`}>
                      <div className={`font-semibold text-slate-800 ${big ? 'text-base leading-tight' : ''}`}>{row.descrizione_caratteristica}</div>
                      {!row.obbligatorio && <span className={big ? 'text-xs text-slate-400' : 'text-[9px] text-slate-400'}>(non obbligatoria)</span>}
                    </td>
                    <td className={`text-center font-mono ${big ? 'p-3' : 'p-1.5'}`}>{row.specifica_nominale || '—'}</td>
                    <td className={`text-center font-mono ${big ? 'p-3' : 'p-1.5'}`}>{row.tolleranza_min || '—'}</td>
                    <td className={`text-center font-mono ${big ? 'p-3' : 'p-1.5'}`}>{row.tolleranza_max || '—'}</td>
                    <td className={`text-center text-slate-500 ${big ? 'p-3' : 'p-1.5'}`}>{row.unita_misura || ''}</td>
                    {visibleSamples.map((s, ci) => {
                      const k = cellKey(s.id, row.id);
                      const saved = measurementsMap[k];
                      const savedResult = saved?.result;
                      const cellBg = savedResult === 'OK' ? 'bg-emerald-50 border-emerald-300' : savedResult === 'NOK' ? 'bg-red-50 border-red-300' : 'bg-white';
                      const isActive = active.r === ri && active.c === ci;
                      return (
                        <td key={s.id} className={`text-center relative ${big ? 'p-2' : 'p-1'} ${active.c === ci ? 'bg-indigo-50/40' : ''}`}>
                          <input
                            ref={el => { cellRefs.current[`${ri}:${ci}`] = el; }}
                            type="text"
                            inputMode={isNumeric ? 'decimal' : 'text'}
                            value={values[k] ?? ''}
                            disabled={locked}
                            onChange={e => onCellChange(s.id, row, e.target.value)}
                            onFocus={() => setActive({ r: ri, c: ci })}
                            onBlur={() => { setActive({ r: -1, c: -1 }); onCellBlur(s.id, row); }}
                            onKeyDown={e => onCellKeyDown(e, ri, ci, s, row)}
                            className={`w-full text-center font-mono rounded border ${cellBg} ${isActive ? 'ring-2 ring-indigo-500 border-indigo-500' : ''} disabled:bg-slate-100 ${big ? 'h-14 text-xl px-2 border-2' : 'text-xs px-1 py-0.5'}`}
                            data-testid={`qcr-cell-${s.sample_number}-${row.numero_caratteristica}`}
                          />
                          {savedResult && (
                            <div className={`mt-0.5 flex items-center justify-center gap-1 ${big ? 'text-xs' : 'text-[9px]'}`}>
                              {resultBadge(savedResult)}
                              {savedResult === 'NOK' && saved?.id && (
                                <button
                                  type="button"
                                  onClick={() => openPending(saved, row, s)}
                                  className={`text-orange-700 hover:text-orange-900 underline ${big ? 'p-2' : ''}`}
                                  title="Crea Pending Collaudo"
                                  data-testid={`qcr-pc-${s.sample_number}-${row.numero_caratteristica}`}
                                ><Wrench className={big ? 'w-5 h-5' : 'w-3 h-3'} /></button>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {planRows.length === 0 && (
                <tr><td colSpan={6 + visibleSamples.length} className="text-center py-6 text-slate-400">Il Piano di Controllo non ha caratteristiche attive.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        <div className="text-xs text-slate-500 space-y-1">
          <div>
            <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-600" />
            Ogni valore inserito viene <b>salvato automaticamente</b> (circa 0,7 s dopo la digitazione o all&apos;uscita dal campo). Le celle diventano <span className="text-emerald-700 font-semibold">verdi (OK)</span> o <span className="text-red-700 font-semibold">rosse (NOK)</span> in base alle tolleranze del piano. Senza rete le misure restano in coda sul dispositivo e vengono sincronizzate al ritorno della connessione.
          </div>
          <div className="hidden md:flex flex-wrap gap-x-4 gap-y-1 items-center" data-testid="qcr-shortcuts-legend">
            <span className="font-semibold text-slate-600">Navigazione rapida:</span>
            {(viewMode === 'single'
              ? [['Invio', 'quota successiva'], ['Maiusc+Invio', 'quota precedente'], ['Esc', 'annulla valore']]
              : [['Tab', 'campione successivo'], ['Invio', 'quota successiva'], ['Maiusc+Invio', 'quota precedente'],
                 ['↑ ↓', 'su / giù'], ['Ctrl+← →', 'campione prec. / succ.'], ['Ctrl+Invio', 'prossima misura vuota'],
                 ['Esc', 'annulla valore']]
            ).map(([k, d]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 font-mono text-[10px]">{k}</kbd>
                <span className="text-[11px]">{d}</span>
              </span>
            ))}
            <span className="text-[11px] text-slate-400">— si può usare la virgola come separatore decimale</span>
          </div>
        </div>

        {/* SIGNATURES */}
        <div className="bg-white rounded border p-3" data-testid="qcr-signatures-block">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-slate-800">Firme report</h2>
            {rep.status !== 'Completato' && (
              <span className="text-[11px] text-slate-500">
                {rep.status === 'Annullato' ? 'Report annullato: non firmabile' : 'Disponibili dopo il completamento del report'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[['inspector', 'Ispettore'], ['quality_manager', 'Responsabile Qualità']].map(([role, label]) => {
              const sig = rep.signatures?.[role];
              return (
                <div key={role} className="border rounded p-3 bg-slate-50" data-testid={`qcr-signature-${role}`}>
                  <div className="text-xs font-semibold text-slate-700 mb-2">{label}</div>
                  {sig ? (
                    <div className="space-y-1">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${sig.url}?auth=${localStorage.getItem('evotech_token')}`}
                        alt={`Firma ${label}`}
                        className="h-16 object-contain bg-white border rounded"
                        data-testid={`qcr-signature-img-${role}`}
                      />
                      <div className="text-[11px] text-slate-600">
                        {sig.signed_by_name} — {new Date(sig.signed_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {canManage && (
                        <Button size="sm" variant="outline" className="text-red-700 h-7 text-[11px]" onClick={() => removeSignature(role)} data-testid={`qcr-signature-delete-${role}`}>
                          Rimuovi firma
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={rep.status !== 'Completato' || (role === 'quality_manager' && !canManage)}
                      onClick={() => { setSignData(null); setSignOpen(role); }}
                      data-testid={`qcr-sign-btn-${role}`}
                    >
                      <PenLine className="w-3.5 h-3.5 mr-1" />Firma Report
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Conflict dialog */}
      <Dialog open={!!conflict} onOpenChange={o => !o && setConflict(null)}>
        <DialogContent data-testid="qcr-conflict-dialog">
          <DialogHeader><DialogTitle>Conflitto di salvataggio</DialogTitle></DialogHeader>
          {conflict && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">{conflict.message}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="border rounded p-2 bg-slate-50">
                  <div className="font-semibold mb-1">Valore sul server</div>
                  <div className="font-mono text-base">{conflict.server_measurement?.measured_numeric_value ?? conflict.server_measurement?.measured_text_value ?? '—'}</div>
                  <div className="text-slate-500 mt-1">{conflict.server_updated_by || 'altro utente'}</div>
                </div>
                <div className="border rounded p-2 bg-amber-50">
                  <div className="font-semibold mb-1">Il tuo valore</div>
                  <div className="font-mono text-base">{conflict.incoming?.measured_numeric_value ?? conflict.incoming?.measured_text_value ?? '—'}</div>
                  <div className="text-slate-500 mt-1">non ancora salvato</div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => resolveConflict('server')} data-testid="qcr-conflict-keep-server">Tieni valore server</Button>
            <Button onClick={() => resolveConflict('mine')} data-testid="qcr-conflict-keep-mine">Tieni il mio valore</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incomplete report dialog */}
      <Dialog open={!!incomplete} onOpenChange={o => !o && setIncomplete(null)}>
        <DialogContent data-testid="qcr-incomplete-dialog">
          <DialogHeader><DialogTitle>Report incompleto</DialogTitle></DialogHeader>
          {incomplete && (
            <div className="space-y-2 text-sm">
              <p className="text-slate-700">{incomplete.message}</p>
              <p className="text-xs text-slate-500">Elementi mancanti: <b>{incomplete.missing_count}</b> — nessuna misura già inserita è stata modificata.</p>
              <div className="max-h-64 overflow-auto border rounded text-xs">
                <table className="w-full">
                  <thead className="bg-slate-100"><tr><th className="p-1.5 text-left">Campione</th><th className="p-1.5 text-left">Caratteristica</th></tr></thead>
                  <tbody>
                    {(incomplete.missing || []).map((m, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-1.5">{m.sample_number ? `C${String(m.sample_number).padStart(2, '0')}` : '—'}</td>
                        <td className="p-1.5">{m.numero_caratteristica ? `${m.numero_caratteristica} — ` : ''}{m.descrizione_caratteristica}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncomplete(null)}>Chiudi</Button>
            <Button onClick={() => { setIncomplete(null); focusFirstEmpty(); }} data-testid="qcr-incomplete-goto">Vai alla prima misura mancante</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature dialog */}
      <Dialog open={!!signOpen} onOpenChange={o => { if (!o) { setSignOpen(null); setSignData(null); } }}>
        <DialogContent data-testid="qcr-signature-dialog">
          <DialogHeader>
            <DialogTitle>Firma report — {signOpen === 'quality_manager' ? 'Responsabile Qualità' : 'Ispettore'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs bg-slate-50 border rounded p-2">
              <div><b>Report</b>: {rep.report_number} — esito <b>{rep.overall_result || 'n/d'}</b></div>
              <div><b>Prodotto</b>: {rep.product_code} — Lotto {rep.lot_code || '—'}</div>
            </div>
            <SignaturePad onChange={setSignData} />
            <p className="text-[11px] text-slate-500">La firma verrà salvata con nome, data e ora e stampata sul PDF del report.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSignOpen(null); setSignData(null); }}>Annulla</Button>
            <Button onClick={submitSignature} disabled={!signData} data-testid="qcr-signature-submit">Conferma firma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Collaudo dialog */}
      <Dialog open={!!pcOpen} onOpenChange={o => !o && setPcOpen(null)}>
        <DialogContent data-testid="qcr-pc-dialog">
          <DialogHeader><DialogTitle>Crea Pending Collaudo</DialogTitle></DialogHeader>
          {pcOpen && (
            <div className="space-y-3">
              <div className="text-xs bg-slate-50 border rounded p-2">
                <div><b>Report</b>: {rep.report_number}</div>
                <div><b>Campione</b>: C{String(pcOpen.sample.sample_number).padStart(2, '0')} {pcOpen.sample.sample_identifier && `(${pcOpen.sample.sample_identifier})`}</div>
                <div><b>Caratteristica</b>: {pcOpen.row.descrizione_caratteristica}</div>
                <div><b>Misura NOK</b>: {pcOpen.measurement.measured_numeric_value ?? pcOpen.measurement.measured_text_value ?? '—'}</div>
              </div>
              <div>
                <Label>Azione richiesta *</Label>
                <Textarea value={pcForm.requested_action} onChange={e => setPcForm({ ...pcForm, requested_action: e.target.value })} placeholder="Es. rilavorare pezzo, sostituire utensile…" data-testid="qcr-pc-action" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Priorità</Label>
                  <select value={pcForm.priority} onChange={e => setPcForm({ ...pcForm, priority: e.target.value })} className="w-full h-9 rounded border px-2 text-sm">
                    <option value="low">Bassa</option><option value="normal">Normale</option><option value="high">Alta</option><option value="urgent">Urgente</option>
                  </select>
                </div>
                <div>
                  <Label>Scadenza</Label>
                  <Input type="date" value={pcForm.deadline} onChange={e => setPcForm({ ...pcForm, deadline: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Nota KO</Label>
                <Input value={pcForm.ko_note} onChange={e => setPcForm({ ...pcForm, ko_note: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPcOpen(null)}>Annulla</Button>
            <Button onClick={submitPending} data-testid="qcr-pc-submit">Crea Pending</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
